import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	Empty,
	GraphQLErrorMessage,
	Jumbo,
	tokens,
} from '@treams/treams-ui';

import Authentication from './Authentication';
import PersonalInformation from './PersonalInformation';

const Grid = styled(Flex)`
	padding: 16px;
	border-radius: 5px;
	border: 1px solid ${tokens.border.subtle};
	align-items: center;
	flex-direction: column;
`;

export default function CreateEmployee() {
	const { t } = useTranslation();
	const { language } = useLocalize();

	const groups = graphql.useGroupsQuery({
		variables: { first: 1000, filter: { types: [graphql.GroupType.Department] } },
	});

	const identityProviders = graphql.useIdentityProvidersQuery();

	const user = groups.data
		? {
				firstName: '',
				lastName: '',
				activatedAt: new Date(),
				username: '',
				email: '',
				locale: language.id,
				group: groups.data.groups.edges.find(({ node }) => !node.parent)?.node,
				externalReferenceID: undefined as string | undefined,
				isExternal: false,
				gender: undefined as graphql.Gender | undefined,
				associatedIdentities: [] as graphql.AssociatedIdentity[],
			}
		: undefined;

	const [valid, setValid] = useState(false);
	const [missing, setMissing] = useState<string | undefined>();

	const form = useRef({ ...user! }).current;

	const [error, attempt, reset] = useErrorBoundary();

	function updateForm(values: Partial<typeof form>) {
		Object.assign(form, values);

		reset();

		setValid(
			!(
				form.firstName.length === 0 ||
				form.lastName.length === 0 ||
				form.username.length === 0 ||
				form.email.length === 0
			),
		);

		if (form?.firstName.length === 0) {
			setMissing(t('settings.employees.create.missing.firstName'));
		} else if (form?.lastName.length === 0) {
			setMissing(t('settings.employees.create.missing.lastName'));
		} else if (form?.username.length === 0) {
			setMissing(t('settings.employees.create.missing.username'));
		} else if (form?.email.length === 0) {
			setMissing(t('settings.employees.create.missing.email'));
		} else {
			setMissing(undefined);
		}
	}

	useEffect(() => {
		if (user) {
			updateForm(user!);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!groups.data]);

	const [associate] = graphql.useEnsureAssociatedIdentitiesMutation();
	const [create] = graphql.useCreateUserMutation();

	const history = useHistory();

	return (
		<>
			<Jumbo title={t('settings.employees.create.title')} />
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<Flex mb={4}>
					<Box flexGrow={1} flexShrink={1} flexBasis={0} mr={4}>
						<PersonalInformation create user={user as graphql.User} onChange={updateForm} />
					</Box>
					<Box flexGrow={1} flexShrink={1} flexBasis={0}>
						<Authentication
							create
							user={user as graphql.User}
							onChange={updateForm}
							identityProviders={
								identityProviders.data?.identityProviders as graphql.IdentityProvider[]
							}
						/>
					</Box>
				</Flex>
				{error && (
					<Box mb={2}>
						<GraphQLErrorMessage error={error} />
					</Box>
				)}
				<Flex mb={4}>
					<Flex>
						<Button
							type="primary"
							title={t('common.save')}
							disabled={!valid}
							onClick={attempt(async () => {
								const response = await create({
									variables: {
										input: {
											firstName: form.firstName,
											lastName: form.lastName,
											email: form.email,
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
											groupID: (form as any).groupID ?? form.group!.id,
											locale: form.locale,
											username: form.username,
											activatedAt: form.activatedAt,
											externalReferenceID: form.externalReferenceID,
											isExternal: form.isExternal,
											gender: form.gender,
										},
									},
								});

								const id = response.data!.createUser.user.id;
								if (identityProviders.data!.identityProviders.length > 0) {
									await associate({
										variables: {
											input: {
												userID: id,
												identityProviderID: identityProviders.data!.identityProviders[0].id,
												names: form.associatedIdentities.map(({ name }) => name),
											},
										},
									});
								}

								history.push(`/settings/employees/${id}`);
							})}
						/>
						{missing && (
							<Callout color="primary" ml={2}>
								{missing}
							</Callout>
						)}
					</Flex>
				</Flex>
				<Grid>
					<Empty description={t('settings.employees.create.placeholder')} />
				</Grid>
			</Container>
		</>
	);
}
